import React, { useCallback, useState } from 'react';
import { supportTicketService } from 'api/services/SupportTicketService';
import { moderationService } from 'api/services/ModerationService';

/** Seeded category: Şikayet (SupportConstants.CategoryIds.Complaint). */
const SUPPORT_CATEGORY_COMPLAINT = 3;

const DETAILS_MAX_LENGTH = 2000;

export interface ChatUserComplaintModalProps {
  reportedUserId: string;
  onClose: () => void;
  onSubmitted: () => void;
}

/**
 * Chat-specific complaint flow: support ticket (Şikayet category) + block the peer.
 */
const ChatUserComplaintModal: React.FC<ChatUserComplaintModalProps> = ({
  reportedUserId,
  onClose,
  onSubmitted,
}) => {
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const trimmed = details.trim();
  const overLimit = trimmed.length > DETAILS_MAX_LENGTH;
  const canSubmit = trimmed.length > 0 && !overLimit && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      setErrorMessage('Lütfen şikayetinizi yazın.');
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const ticketRes = await supportTicketService.create({
        supportCategoryId: SUPPORT_CATEGORY_COMPLAINT,
        details: trimmed,
      });
      if (!ticketRes.isSuccess) {
        setErrorMessage(ticketRes.message ?? 'Şikayet kaydedilemedi.');
        return;
      }

      const blockRes = await moderationService.blockUser({ blockedUserId: reportedUserId });
      if (!blockRes.isSuccess) {
        setErrorMessage(blockRes.message ?? 'Şikayet alındı ancak engelleme başarısız oldu.');
        return;
      }

      onSubmitted();
      onClose();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, trimmed, reportedUserId, onSubmitted, onClose]);

  return (
    <>
      <div
        className="modal-backdrop fade show"
        aria-hidden="true"
        onClick={submitting ? undefined : onClose}
      />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chatUserComplaintModalTitle"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="chatUserComplaintModalTitle">
                <i className="fas fa-flag text-primary me-2" />
                Kullanıcı Şikayeti
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Kapat"
                disabled={submitting}
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              <label className="form-label fw-medium" htmlFor="chat-complaint-details">
                Şikayet detayları
              </label>
              <textarea
                id="chat-complaint-details"
                className="form-control"
                rows={6}
                placeholder="Lütfen şikayetinizi detaylı bir şekilde açıklayın..."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                disabled={submitting}
                maxLength={DETAILS_MAX_LENGTH}
              />
              <div className="form-text">
                Şikayetiniz gizli tutulur ve yalnızca yetkili moderatörler tarafından incelenir.
              </div>
              {overLimit && (
                <p className="text-danger small mb-0 mt-2">
                  En fazla {DETAILS_MAX_LENGTH} karakter girebilirsiniz.
                </p>
              )}
              {errorMessage && (
                <p className="text-danger small mb-0 mt-2" role="alert">{errorMessage}</p>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" disabled={submitting} onClick={onClose}>
                İptal
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canSubmit}
                onClick={() => void handleSubmit()}
              >
                {submitting ? 'Gönderiliyor…' : 'Şikayet Et ve Engelle'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatUserComplaintModal;
